/**
 * BatchDetailPage — single-batch view at /trainees/batches/:batchId
 *
 * Layout (top → bottom):
 *   • Header: breadcrumb back to /trainees + batch name + status pill + actions
 *     (Edit / Archive / Import / Export this batch / + Add Trainee)
 *   • Summary cards (8): total trainees · avg attendance % · avg assessment %
 *     · cert % · training completion % · start date · end date · trainers
 *   • Trainees table — same columns as the global TraineesTab, filtered to
 *     this batch. Per-row actions: View dashboard, Edit, Move to another
 *     batch, Delete.
 *
 * Data sources:
 *   • GET /api/v1/trainees/batches/{id}                  — batch metadata
 *   • GET /api/v1/trainees/dashboard/batches/{id}         — aggregated KPIs
 *   • GET /api/v1/trainees?batch_id={id}                 — filtered trainees
 *   • GET /api/v1/trainees/fields                        — column definitions
 *   • GET /api/v1/users?role=trainer                     — trainer name lookup
 *
 * Per the user's enhancement spec (Phase 1 — June 28 2026):
 *   - Two trainer workflows must both work: (a) create trainee → assign to
 *     batch (global Trainees tab) and (b) create batch → open → add trainee
 *     directly (this page). The TraineeFormDialog accepts `lockBatchId` to
 *     hide the batch picker when we're already scoped to a batch.
 *   - "Move trainee to another batch" reuses the standard PUT /trainees/{id}
 *     contract — no new endpoint needed.
 *   - "Archive batch" maps to PUT /trainees/batches/{id} with status=archived.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Archive, BarChart3, Download, FolderInput, Pencil, Plus,
  Trash2, Upload, Users, UsersRound, FileArchive, RefreshCcw,
} from 'lucide-react';

import api from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { ImportPreviewDialog } from './ImportPreviewDialog';
import BulkEnrolDialog from './BulkEnrolDialog';
import { ExportTraineesDialog } from './ExportTraineesDialog';
import { AttemptedChip } from './AttemptedChip';
import { ReconcileResultsDialog } from './ReconcileResultsDialog';

import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

import TraineeDashboardDialog from './TraineeDashboardDialog';
import { TraineeFormDialog, renderFieldValue } from './TraineeFormDialog';

const STATUS_TONES = {
  active:   'bg-amber-100 text-amber-700 border-amber-200',
  closed:   'bg-slate-100 text-slate-700 border-slate-200',
  archived: 'bg-slate-200 text-slate-500 border-slate-300',
};

function StatCard({ label, value, accent }) {
  return (
    <Card className="p-4" data-testid={`batch-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`font-display tabular-nums text-2xl font-bold mt-1 ${accent || ''}`}>{value}</p>
    </Card>
  );
}

export default function BatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canEdit = hasRole('trainer');
  const canArchive = hasRole('org_admin');

  const [batch, setBatch] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [trainees, setTrainees] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fields, setFields] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add / edit dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', employee_id: '', batch_id: '', fields: {} });

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);   // trainee being enrolled elsewhere
  const [moveToBatchId, setMoveToBatchId] = useState('');
  const [moveRemoveOld, setMoveRemoveOld] = useState(false);
  const [dashboardFor, setDashboardFor] = useState(null);
  const [dashboardFocus, setDashboardFocus] = useState(null);
  const [coverage, setCoverage] = useState({});

  // Import / export
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [bulkEnrolOpen, setBulkEnrolOpen] = useState(false);
  const [packing, setPacking] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [dups, setDups] = useState(null);
  const [merging, setMerging] = useState(false);

  const loadAll = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const [b, d, t, bs, fs] = await Promise.all([
        get(`/trainees/batches/${batchId}`),
        get(`/trainees/dashboard/batches/${batchId}`).catch(() => null),
        get(`/trainees?batch_id=${batchId}&limit=500`),
        get('/trainees/batches'),
        get('/trainees/fields'),
      ]);
      setBatch(b);
      setDashboard(d);
      setTrainees(t.trainees || []);
      setBatches(bs.batches || []);
      setFields(fs.fields || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load batch');
      // Bounce back if the batch_id is bogus so the user isn't stranded.
      if (e.response?.status === 404) navigate('/trainees', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [get, batchId, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (trainees.length === 0) { setCoverage({}); return; }
    post('/trainees/assessment-coverage', { trainee_ids: trainees.map(t => t.trainee_id), batch_id: batchId })
      .then(r => setCoverage(r.coverage || {})).catch(() => setCoverage({}));
  }, [trainees, batchId, post]);

  const openAssessmentHistory = (t) => { setDashboardFocus('assessments'); setDashboardFor(t); };

  // Trainer-name lookup, kept best-effort — if the user doesn't have access
  // to /users (trainer-only role), we silently show user_ids instead of names.
  useEffect(() => {
    if (!batch?.trainer_ids?.length) return;
    get('/users?limit=200').then(r => setUsers(r.users || [])).catch(() => {});
  }, [batch?.trainer_ids, get]);

  const summaryFields = useMemo(
    () => fields.filter(f => f.is_visible && ['training_status', 'certification_status', 'alert_status'].includes(f.field_key)),
    [fields]
  );

  const trainerNames = useMemo(() => {
    if (!batch?.trainer_ids?.length) return '—';
    if (!users.length) return `${batch.trainer_ids.length} assigned`;
    const byId = Object.fromEntries(users.map(u => [u.user_id, `${u.first_name} ${u.last_name}`.trim()]));
    const named = batch.trainer_ids.map(id => byId[id]).filter(Boolean);
    return named.length ? named.join(', ') : `${batch.trainer_ids.length} assigned`;
  }, [batch?.trainer_ids, users]);

  // Derive completion + cert percentages — the batch dashboard surfaces
  // cert_funnel (list of {key, count}) but not training-completion, so we
  // compute completion straight from the loaded trainees array.
  const completionPct = useMemo(() => {
    if (!trainees.length) return null;
    const complete = trainees.filter(t => {
      const s = ((t.fields || {}).training_status || '').toString().toLowerCase();
      return /complete|completed|certified|passed|done/.test(s);
    }).length;
    return Math.round((complete / trainees.length) * 100);
  }, [trainees]);

  const certPct = useMemo(() => {
    if (!dashboard?.cert_funnel?.length) return null;
    const total = dashboard.cert_funnel.reduce((s, r) => s + (r.count || 0), 0);
    if (!total) return 0;
    const certified = dashboard.cert_funnel
      .filter(r => /certified|passed|complete/i.test(r.key || ''))
      .reduce((s, r) => s + (r.count || 0), 0);
    return Math.round((certified / total) * 100);
  }, [dashboard]);

  // ── Add / edit trainee (batch is locked to this page's batchId) ──
  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', employee_id: '', batch_id: batchId, fields: {} });
    setOpenDialog(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '', email: t.email || '', employee_id: t.employee_id || '',
      batch_id: t.batch_id || batchId, fields: { ...(t.fields || {}) },
    });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = {
      name: form.name.trim(),
      email: form.email || null,
      employee_id: form.employee_id || null,
      batch_id: batchId,
      fields: form.fields || {},
    };
    try {
      if (editing) {
        await put(`/trainees/${editing.trainee_id}?for_batch_id=${batchId}`, payload);
        toast.success('Trainee updated');
      } else {
        const created = await post('/trainees', payload);
        toast.success(created?.enrolled_existing ? created.message : 'Trainee added to batch');
      }
      setOpenDialog(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/${confirmDelete.trainee_id}`);
      toast.success('Trainee deleted');
      setConfirmDelete(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const onRemoveFromBatch = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/${confirmDelete.trainee_id}/enrollments/${batchId}`);
      toast.success(`Removed ${confirmDelete.name} from this batch — history kept`);
      setConfirmDelete(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
  };

  const onMove = async () => {
    if (!moveTarget || !moveToBatchId || moveToBatchId === batchId) {
      setMoveTarget(null);
      return;
    }
    try {
      await post(`/trainees/${moveTarget.trainee_id}/enrollments`, {
        batch_id: moveToBatchId, make_current: true,
        remove_from_batch_id: moveRemoveOld ? batchId : null,
      });
      toast.success(moveRemoveOld
        ? `Moved ${moveTarget.name} to another batch`
        : `${moveTarget.name} enrolled in another batch — stays on this roster too`);
      setMoveTarget(null);
      setMoveToBatchId('');
      setMoveRemoveOld(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enrol failed');
    }
  };

  const onArchive = async () => {
    try {
      await put(`/trainees/batches/${batchId}`, { status: 'archived' });
      toast.success('Batch archived');
      setConfirmArchive(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/trainees/bulk/import?dry_run=true&batch_id=${batchId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview({ preview: res.data, file });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onConfirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importPreview.file);
      const res = await api.post(`/trainees/bulk/import?batch_id=${batchId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      const added = (d.created || 0) + (d.enrolled_existing || 0);
      toast.success(added
        ? `${added} agent${added === 1 ? '' : 's'} added to ${batch?.name || 'this batch'}`
          + (d.enrolled_existing ? ` (${d.enrolled_existing} returning — starting with a clean status)` : '')
          + (d.updated ? `, ${d.updated} updated` : '')
        : `No agents added — ${d.updated || 0} already here were updated`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      setImportPreview(null);
    }
  };

  const onExport = async (includeAll = false) => {
    try {
      const res = await api.get(`/trainees/bulk/export?batch_id=${batchId}&include_all_assessments=${includeAll}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (batch?.name || 'batch').replace(/[^a-zA-Z0-9-_ ]/g, '_').slice(0, 60);
      a.download = `${safe}-trainees-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch {
      toast.error('Export failed');
    }
  };

  useEffect(() => {
    if (!batchId || !canEdit) return;
    get(`/trainees/batches/${batchId}/duplicates`).then(setDups).catch(() => setDups(null));
  }, [batchId, trainees, canEdit, get]);

  const onMergeDuplicates = async () => {
    setMerging(true);
    try {
      const r = await post(`/trainees/batches/${batchId}/merge-duplicates`, {});
      toast.success(`${r.people} agent${r.people === 1 ? '' : 's'} consolidated — ${r.removed} duplicate record${r.removed === 1 ? '' : 's'} removed, scores recovered`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally { setMerging(false); }
  };

  const onHistoryPack = async () => {
    setPacking(true);
    try {
      const res = await api.get(`/trainees/batches/${batchId}/history-pack.zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (batch?.name || 'batch').replace(/[^a-zA-Z0-9-_ ]/g, '_').slice(0, 60);
      a.download = `training-history-${safe}-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`History pack ready — ${trainees.length} PDF${trainees.length === 1 ? '' : 's'} + index.csv`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build history pack');
    } finally { setPacking(false); }
  };

  if (loading) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Loading batch…</p>;
  }
  if (!batch) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground">Batch not found.</p>
        <Button variant="outline" className="mt-3" onClick={() => navigate('/trainees')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Trainees
        </Button>
      </div>
    );
  }

  const otherBatches = batches.filter(b => b.batch_id !== batchId);
  const tone = STATUS_TONES[batch.status] || STATUS_TONES.active;

  return (
    <div className="space-y-6" data-testid="batch-detail-page">
      {/* Breadcrumb + header */}
      <div className="space-y-2">
        <button
          onClick={() => navigate('/trainees')}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid="batch-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Trainees / Batches
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="batch-heading">
                {batch.name}
              </h1>
              <Badge variant="outline" className={`uppercase ${tone}`} data-testid="batch-status-pill">
                {batch.status}
              </Badge>
            </div>
            {(batch.department || batch.designation) && (
              <p className="text-sm text-muted-foreground mt-1">
                {batch.department}{batch.department && batch.designation ? ' · ' : ''}{batch.designation}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
                   onChange={onImport} data-testid="batch-import-input" />
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}
                        disabled={importing} data-testid="batch-import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Importing…' : 'Import'}
                </Button>
                <Button variant="outline" onClick={() => setShowExport(true)} data-testid="batch-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button variant="outline" onClick={onHistoryPack} disabled={packing || trainees.length === 0}
                        data-testid="batch-history-pack-btn"
                        title="Download one training-history PDF per agent as a zip (for HR / client audits)">
                  <FileArchive className="w-4 h-4 mr-2" /> {packing ? 'Packing…' : 'History pack'}
                </Button>
                <Button variant="outline" onClick={() => setShowReconcile(true)} data-testid="batch-reconcile-btn"
                        title="Find submitted assessments not credited to anyone on this roster and restore them">
                  <RefreshCcw className="w-4 h-4 mr-2" /> Reconcile results
                </Button>
                {otherBatches.length > 0 && trainees.length > 0 && (
                  <Button variant="outline" onClick={() => setBulkEnrolOpen(true)}
                          data-testid="batch-bulk-enrol-btn" title="Enrol everyone on this roster into another batch">
                    <UsersRound className="w-4 h-4 mr-2" /> Enrol all into…
                  </Button>
                )}
              </>
            )}
            {canArchive && batch.status !== 'archived' && (
              <Button variant="outline" onClick={() => setConfirmArchive(true)}
                      data-testid="batch-archive-btn">
                <Archive className="w-4 h-4 mr-2" /> Archive
              </Button>
            )}
            {canEdit && (
              <Button onClick={openCreate} data-testid="batch-add-trainee-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Trainee
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {canEdit && dups?.count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border rounded-md px-4 py-2.5 text-sm"
             data-testid="batch-duplicates-banner">
          <span className="text-muted-foreground">
            <strong className="text-foreground">{dups.count} agent{dups.count === 1 ? '' : 's'}</strong> on this roster
            {dups.count === 1 ? ' has' : ' have'} a second record — scores may be split.
          </span>
          <div className="flex items-center gap-3">
            <Link to="/trainees" className="text-xs underline text-muted-foreground"
                  data-testid="batch-duplicates-review-link">Review in Duplicates</Link>
            <Button size="sm" onClick={onMergeDuplicates} disabled={merging} data-testid="batch-merge-duplicates-btn">
              {merging ? 'Merging…' : `Merge ${dups.count} duplicate${dups.count === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Trainees" value={batch.trainee_count ?? trainees.length} />
        <StatCard
          label="Avg Attendance"
          value={dashboard?.avg_attendance_pct != null ? `${dashboard.avg_attendance_pct}%` : '—'}
          accent={dashboard?.avg_attendance_pct < 80 ? 'text-rose-600' : ''}
        />
        <StatCard
          label="Avg Assessment"
          value={dashboard?.avg_assessment_pct != null ? `${dashboard.avg_assessment_pct}%` : '—'}
        />
        <StatCard
          label="Certification %"
          value={certPct != null ? `${certPct}%` : '—'}
        />
        <StatCard
          label="Training Complete %"
          value={completionPct != null ? `${completionPct}%` : '—'}
        />
        <StatCard
          label="Start Date"
          value={batch.start_date ? new Date(batch.start_date).toLocaleDateString() : '—'}
        />
        <StatCard
          label="End Date"
          value={batch.end_date ? new Date(batch.end_date).toLocaleDateString() : '—'}
        />
        <Card className="p-4" data-testid="batch-stat-assigned-trainers">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned Trainers</p>
          <p className="text-sm font-medium mt-1 line-clamp-2" title={trainerNames}>{trainerNames}</p>
        </Card>
      </div>

      {/* Trainees table */}
      <Card>
        {trainees.length === 0 ? (
          <div className="p-12 text-center" data-testid="batch-trainees-empty">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-display text-lg font-semibold">No trainees in this batch yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add trainees one-by-one or import from your Google Sheet / Excel.
            </p>
            {canEdit && (
              <Button onClick={openCreate} className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Add First Trainee
              </Button>
            )}
          </div>
        ) : (
          <Table data-testid="batch-trainees-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Cisco ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Assessments</TableHead>
                {summaryFields.map(f => (
                  <TableHead key={f.field_id}>{f.label}</TableHead>
                ))}
                {canEdit && <TableHead className="w-32 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainees.map(t => (
                <TableRow key={t.trainee_id} data-testid={`batch-trainee-row-${t.trainee_id}`}>
                  <TableCell className="font-medium">
                    {t.name}
                    {(t.batch_ids || []).length > 1 && (
                      <Badge variant="outline" className="ml-2 text-[10px] font-normal"
                             title="Enrolled in more than one batch"
                             data-testid={`batch-trainee-multi-${t.trainee_id}`}>
                        +{t.batch_ids.length - 1} batch{t.batch_ids.length > 2 ? 'es' : ''}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{t.employee_id || '—'}</TableCell>
                  <TableCell className="text-sm">{t.email || '—'}</TableCell>
                  <TableCell>
                    <AttemptedChip coverage={coverage[t.trainee_id]} onClick={() => openAssessmentHistory(t)}
                                   testId={`batch-attempted-chip-${t.trainee_id}`} />
                  </TableCell>
                  {summaryFields.map(f => (
                    <TableCell key={f.field_id} className="text-sm">
                      {renderFieldValue(f, (t.fields || {})[f.field_key])}
                    </TableCell>
                  ))}
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDashboardFor(t)}
                              data-testid={`batch-view-trainee-${t.trainee_id}`}
                              title="View dashboard">
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)}
                              data-testid={`batch-edit-trainee-${t.trainee_id}`}
                              title="Edit trainee">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {otherBatches.length > 0 && (
                        <Button size="sm" variant="ghost"
                                onClick={() => { setMoveTarget(t); setMoveToBatchId(''); setMoveRemoveOld(false); }}
                                data-testid={`batch-move-trainee-${t.trainee_id}`}
                                title="Enrol in another batch">
                          <FolderInput className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(t)}
                              data-testid={`batch-delete-trainee-${t.trainee_id}`}
                              title="Remove from batch / delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Add / edit dialog — batch is locked to this page's batchId */}
      <ExportTraineesDialog open={showExport} onOpenChange={setShowExport} onExport={onExport}
                            scopeLabel={`the ${batch?.name || 'batch'} roster`} />
      <ReconcileResultsDialog open={showReconcile} onOpenChange={setShowReconcile}
                              scope={{ batchId, batchName: batch?.name }} onDone={() => loadAll({ silent: true })} />
      <TraineeFormDialog
        open={openDialog} onOpenChange={setOpenDialog}
        editing={editing} form={form} setForm={setForm}
        batches={batches} fields={fields} onSave={onSave}
        lockBatchId={batchId}
      />

      {/* Trainee dashboard */}
      <TraineeDashboardDialog
        trainee={dashboardFor}
        focus={dashboardFocus}
        onClose={() => { setDashboardFor(null); setDashboardFocus(null); }}
        onChanged={loadAll}
      />

      {/* Remove from batch / delete trainee */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="batch-remove-trainee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name} from this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Remove from batch</strong> takes them off this roster but keeps the person and
              all their training history ({(confirmDelete?.batch_ids || []).length > 1
                ? `they stay enrolled in ${confirmDelete.batch_ids.length - 1} other batch${confirmDelete.batch_ids.length > 2 ? 'es' : ''}`
                : 'they will have no current batch'}).
              <br />
              <strong>Delete permanently</strong> erases the person record everywhere. Prefer removing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={onDelete} data-testid="batch-confirm-delete-trainee-btn">
              Delete permanently
            </Button>
            <AlertDialogAction onClick={onRemoveFromBatch} data-testid="batch-confirm-remove-trainee-btn">
              Remove from batch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive batch */}
      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{batch.name}</strong> will be marked as archived. Existing trainees
              and historical attendance / assessment data stay intact — the batch just
              moves out of the active list. Reversible from the batch list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} data-testid="batch-confirm-archive-btn">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enrol trainee in another batch (optionally moving them) */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent data-testid="batch-move-trainee-dialog">
          <DialogHeader>
            <DialogTitle>Enrol {moveTarget?.name} in another batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Target batch</Label>
              <Select value={moveToBatchId || '__none__'}
                      onValueChange={v => setMoveToBatchId(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="batch-move-target-select">
                  <SelectValue placeholder="Pick a batch…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— pick —</SelectItem>
                  {otherBatches.map(b => (
                    <SelectItem key={b.batch_id} value={b.batch_id}>
                      {b.code ? `${b.code} — ` : ''}{b.name}{b.department ? ` (${b.department})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={moveRemoveOld}
                     onChange={e => setMoveRemoveOld(e.target.checked)}
                     data-testid="batch-move-remove-old-checkbox" />
              <span>
                Also remove from <strong>{batch.name}</strong>
                <span className="block text-xs text-muted-foreground">
                  Tick only if they were added here by mistake. Leave unticked for a second
                  training (e.g. NHT → Global) — they stay on both rosters and history is kept.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)}>Cancel</Button>
            <Button onClick={onMove} disabled={!moveToBatchId}
                    data-testid="batch-confirm-move-btn">
              {moveRemoveOld ? 'Move' : 'Enrol'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {batch && (
        <BulkEnrolDialog open={bulkEnrolOpen} onOpenChange={setBulkEnrolOpen}
                         batch={batch} otherBatches={otherBatches} onDone={loadAll} />
      )}

      {importPreview && (
        <ImportPreviewDialog
          preview={importPreview.preview}
          fileName={importPreview.file.name}
          batchName={batch?.name}
          importing={importing}
          onConfirm={onConfirmImport}
          onCancel={() => setImportPreview(null)}
        />
      )}
    </div>
  );
}
